/**
 * The five things that archive and restore (ADR 032), and how the UI names
 * them. Archive hides a row from lists and pickers and refuses new work on it;
 * history keeps it. Restore puts it back as it was.
 */

export type ArchiveKind = "dataset" | "evaluator" | "experiment" | "queue" | "scoreConfig";

export interface KindInfo {
  /** The API collection: `POST {path}/{id}/archive`, `GET {path}?archived=true`. */
  path: string;
  /** Its list page, for "See archived" links. */
  page: string;
  noun: string;
  plural: string;
  /** The list page's title, for "Hidden from the … list". */
  list: string;
  /** What else hides it, after "Hidden from the … list". */
  hiddenFrom: string;
  /** What `used_by` counts for it, if anything. */
  usedBy?: "experiments" | "queues";
  /** The confirm's line when nothing counts usage. */
  keeps?: string;
  /** Said to someone who may not restore it (ADR 032's who-may table). */
  whoRestores: string;
  /** Above the Archived tab's rows: what archiving kept. */
  archivedNote: string;
}

export const KINDS: Record<ArchiveKind, KindInfo> = {
  dataset: {
    path: "/datasets",
    page: "/datasets",
    noun: "dataset",
    plural: "datasets",
    list: "Datasets",
    hiddenFrom: " and from new experiments",
    usedBy: "experiments",
    whoRestores: "Only editors and owners can restore",
    archivedNote:
      "Archived datasets stay in every past experiment. They don't show up when you set up a new one.",
  },
  evaluator: {
    path: "/evaluators",
    page: "/evaluators",
    noun: "evaluator",
    plural: "evaluators",
    list: "Evaluators",
    hiddenFrom: " and from new experiments",
    usedBy: "experiments",
    whoRestores: "Only editors and owners can restore",
    archivedNote:
      "Archived evaluators keep their scores in every past experiment. They don't show up when you set up a new one.",
  },
  experiment: {
    path: "/experiments",
    page: "/experiments",
    noun: "experiment",
    plural: "experiments",
    list: "Experiments",
    hiddenFrom: ", agent charts and the compare picker",
    keeps: "Its results are kept, and links to it still open.",
    whoRestores: "Only its creator or an admin can restore",
    archivedNote:
      "Archived experiments keep their results, and links to them still open. They're left out of charts and Compare.",
  },
  queue: {
    path: "/annotation-queues",
    page: "/annotation",
    noun: "queue",
    plural: "queues",
    list: "Queues",
    hiddenFrom: "",
    keeps: "Its reviews are kept.",
    whoRestores: "Only its creator or an admin can restore",
    archivedNote: "Archived queues keep their reviews, and links to them still open.",
  },
  scoreConfig: {
    path: "/score-configs",
    page: "/annotation?tab=scores",
    noun: "score",
    plural: "scores",
    list: "Scores",
    hiddenFrom: " and from new queues",
    usedBy: "queues",
    whoRestores: "Only its creator or an admin can restore",
    archivedNote:
      "Archived scores stay on every queue and review that used them. They aren't offered for new queues.",
  },
};

/** The archive fields every archivable row carries (list and detail). */
export interface Archivable {
  archived_at?: string | null | undefined;
  archived_by?: string | null | undefined;
}

/** Only on get-by-id: how many active experiments or queues still use it. */
export interface UsedBy {
  used_by?: { experiments?: number | undefined; queues?: number | undefined } | null | undefined;
}

export const isArchived = (row: Archivable | null | undefined) => Boolean(row?.archived_at);
